<template>
  <div class="download-page bg-slate-50 dark:bg-slate-950 min-h-screen font-sans pb-24 transition-colors">

    <!-- 1. Hero Section -->
    <div class="relative bg-white dark:bg-slate-900 pt-24 pb-20 overflow-hidden border-b border-slate-200 dark:border-slate-800">
      <!-- Dot grid pattern -->
      <div class="absolute inset-0 opacity-[0.4]" style="background-image: radial-gradient(#e5e7eb 1px, transparent 1px); background-size: 20px 20px;"></div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 text-brand-600 text-xs font-bold mb-6 border border-brand-100">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
          <span>全场景覆盖</span>
        </div>

        <h1 class="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tight mb-6 leading-tight">
          全场景覆盖，<br class="md:hidden" />让教学无处不在
        </h1>

        <p class="text-lg md:text-xl text-slate-500 dark:text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
          无论是专业的桌面直播教学，还是灵活的移动端学习，<br class="hidden md:block" />
          魔果云课客户端都能为您提供极致流畅的互动体验。
        </p>
      </div>
    </div>

    <!-- 2. Main Downloads Area -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20 space-y-8">

      <!-- Desktop Card -->
      <div class="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-slate-200/50 dark:shadow-none border border-white dark:border-slate-800 p-8 md:p-12 flex flex-col lg:flex-row gap-12 lg:items-center group overflow-hidden relative">
        <!-- Decor gradient -->
        <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-brand-50 to-blue-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

        <div class="flex-1 relative z-10">
          <div class="flex items-center gap-3 mb-6">
            <div class="w-12 h-12 rounded-2xl bg-brand-100 flex items-center justify-center text-brand-600">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
            </div>
            <div>
              <h2 class="text-2xl font-bold text-slate-900 dark:text-white">桌面端工作台</h2>
              <span class="text-xs font-bold text-brand-600 bg-brand-50 px-2 py-0.5 rounded">
                讲师 / 助教 / 学员通用
              </span>
            </div>
          </div>

          <p class="text-slate-500 dark:text-slate-400 mb-8 leading-relaxed text-base">
            专为大屏设计，集成超高清直播、多路音视频连麦、专业互动白板。
            支持多屏协作，是老师授课与学生沉浸式学习的最佳选择。
          </p>

          <!-- Download Buttons -->
          <div class="flex flex-wrap gap-4 mb-8">
            <!-- Windows -->
            <button
              :class="[
                'group flex items-center gap-4 px-6 py-4 rounded-xl transition-all duration-300 active:scale-95 border',
                isMac
                  ? 'bg-white dark:bg-slate-800 hover:bg-slate-50 text-slate-700 dark:text-white border-slate-200 dark:border-slate-700 shadow-sm'
                  : 'bg-brand-600 hover:bg-brand-700 text-white border-brand-500 shadow-xl shadow-brand-500/20'
              ]"
              @click="download(downloadList[0].url, 'windows')"
            >
              <div :class="['p-2 rounded-lg transition-colors', isMac ? 'bg-slate-100 dark:bg-slate-700' : 'bg-brand-500/30']">
                <img src="../../assets/images/download/Windows_logo_-_2012.svg" class="w-5 h-5" alt="Win" />
              </div>
              <div class="text-left">
                <div class="font-bold text-lg leading-none mb-1.5">Windows</div>
                <div :class="['text-xs font-medium', isMac ? 'text-slate-400' : 'text-brand-100']">支持 Win 10/11 (64位)</div>
              </div>
            </button>

            <!-- Mac -->
            <button
              class="download-mac-button"
              :class="[
                'group flex items-center gap-4 px-6 py-4 rounded-xl transition-all duration-300 active:scale-95 border',
                isMac
                  ? 'bg-brand-600 hover:bg-brand-700 text-white border-brand-500 shadow-xl shadow-brand-500/20'
                  : 'bg-white dark:bg-slate-800 hover:bg-slate-50 text-slate-700 dark:text-white border-slate-200 dark:border-slate-700 shadow-sm'
              ]"
              @click="download(downloadList[1].url, 'mac')"
            >
              <div :class="['p-2 rounded-lg transition-colors', isMac ? 'bg-brand-500/30' : 'bg-slate-100 dark:bg-slate-700']">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path></svg>
              </div>
              <div class="text-left">
                <div class="font-bold text-lg leading-none mb-1.5">macOS</div>
                <div :class="['text-xs font-medium', isMac ? 'text-brand-100' : 'text-slate-400']">支持 Intel & Apple M1/M2</div>
              </div>
            </button>
          </div>

          <div class="flex gap-6 text-sm text-slate-400 font-medium">
            <span class="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              4K 画质
            </span>
            <span class="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              离线缓存
            </span>
            <span class="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              屏幕共享
            </span>
          </div>
        </div>

        <!-- Visual Placeholder (Desktop Preview) -->
        <div class="lg:w-1/2 relative">
          <div class="relative rounded-xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 aspect-[16/10] group-hover:scale-[1.02] transition-transform duration-500">
            <div class="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center">
              <div class="w-[80%] h-[80%] bg-white dark:bg-slate-900 rounded-lg shadow-lg flex flex-col overflow-hidden">
                <div class="h-8 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 flex items-center px-3 gap-2">
                  <div class="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                </div>
                <div class="flex-1 p-4 grid grid-cols-3 gap-4">
                  <div class="col-span-2 bg-slate-100 dark:bg-slate-800 rounded-lg relative overflow-hidden flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="10 8 16 12 10 16 10 8"></polygon></svg>
                  </div>
                  <div class="col-span-1 flex flex-col gap-2">
                    <div class="bg-slate-50 dark:bg-slate-800 h-8 rounded w-full"></div>
                    <div class="bg-slate-50 dark:bg-slate-800 h-8 rounded w-full"></div>
                    <div class="bg-slate-50 dark:bg-slate-800 flex-1 rounded w-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Second Row: Mobile + Specs -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">

        <!-- Mobile Card -->
        <div class="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-xl shadow-slate-200/50 dark:shadow-none border border-white dark:border-slate-800 p-8 flex flex-col items-start gap-8 relative overflow-hidden group">
          <div class="absolute top-0 left-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>

          <!-- Content Wrapper (The "Red Box") -->
          <div class="w-full flex flex-col sm:flex-row items-center justify-between gap-8 relative z-10">
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-6">
                <div class="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-green-600">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                </div>
                <h2 class="text-xl font-bold text-slate-900 dark:text-white">移动端 App</h2>
              </div>
              <p class="text-sm text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
                碎片时间高效利用。完美适配手机与平板，支持作业批改、课程回放与投屏播放。
              </p>
              <div class="flex gap-3">
                <button
                  class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-slate-700 dark:text-slate-200 text-sm font-bold transition-colors border-none cursor-pointer"
                  @click="download(downloadList.find(i => i.type === 'ios')?.url, 'ios')"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  iOS
                </button>
                <button
                  class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-slate-700 dark:text-slate-200 text-sm font-bold transition-colors border-none cursor-pointer"
                  @click="download(downloadList.find(i => i.type === 'android')?.url, 'android')"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  Android
                </button>
              </div>
            </div>

            <!-- QR Code area -->
            <div class="flex gap-6 shrink-0 pr-4">
              <!-- iOS QR -->
              <div class="flex flex-col items-center gap-3">
                <div class="p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.06)] dark:shadow-[0_4px_20px_rgb(0,0,0,0.4)] border border-gray-100 dark:border-slate-700 transition-transform hover:-translate-y-1">
                  <div id="qrcodeCont3" class="w-24 h-24 rounded-lg qr-container"></div>
                </div>
                <span class="text-sm font-medium text-[#80868b] dark:text-slate-400">iOS 下载</span>
              </div>

              <!-- Android QR -->
              <div class="flex flex-col items-center gap-3">
                <div class="p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.06)] dark:shadow-[0_4px_20px_rgb(0,0,0,0.4)] border border-gray-100 dark:border-slate-700 transition-transform hover:-translate-y-1">
                  <div id="qrcodeCont0" class="w-24 h-24 rounded-lg qr-container"></div>
                </div>
                <span class="text-sm font-medium text-[#80868b] dark:text-slate-400">Android 下载</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Specs Card -->
        <div class="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-xl shadow-slate-200/50 dark:shadow-none border border-white dark:border-slate-800 p-8 flex flex-col">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M9 12l2 2 4-4"></path></svg>
              配置检测
            </h2>
            <div class="flex bg-slate-100 p-1 rounded-lg">
              <button
                @click="activeSpecTab = 'teacher'"
                class="px-3 py-1 text-xs font-bold rounded-md transition-all border-none cursor-pointer"
                :class="activeSpecTab === 'teacher' ? 'bg-white text-slate-900 shadow-sm' : 'bg-transparent text-slate-500'"
              >讲师端</button>
              <button
                @click="activeSpecTab = 'student'"
                class="px-3 py-1 text-xs font-bold rounded-md transition-all border-none cursor-pointer"
                :class="activeSpecTab === 'student' ? 'bg-white text-slate-900 shadow-sm' : 'bg-transparent text-slate-500'"
              >学员端</button>
            </div>
          </div>

          <div class="space-y-4">
            <div v-for="spec in currentSpecs" :key="spec.label" class="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div class="flex items-center gap-3">
                <span class="text-slate-400"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><rect x="9" y="9" width="6" height="6"></rect><line x1="9" y1="1" x2="9" y2="4"></line><line x1="15" y1="1" x2="15" y2="4"></line><line x1="9" y1="20" x2="9" y2="23"></line><line x1="15" y1="20" x2="15" y2="23"></line><line x1="20" y1="9" x2="23" y2="9"></line><line x1="20" y1="14" x2="23" y2="14"></line><line x1="1" y1="9" x2="4" y2="9"></line><line x1="1" y1="14" x2="4" y2="14"></line></svg></span>
                <span class="text-sm font-bold text-slate-600 dark:text-slate-300">{{ spec.label }}</span>
              </div>
              <span class="text-sm font-medium text-slate-500 dark:text-slate-400 text-right">{{ spec.value }}</span>
            </div>
          </div>
        </div>

      </div>

      <!-- Help Links -->
      <div class="pt-8 pb-4 text-center">
        <p class="text-slate-400 text-sm">
          遇到安装问题？
          <a href="/help" class="text-brand-600 hover:underline font-bold mx-1">查看帮助文档</a>
        </p>
      </div>

    </div>

    <!-- Mac Chip Selection Dialog -->
    <el-dialog
      :visible.sync="selectMacTypeVisible"
      width="933px"
      class="mac-type-dialog"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="content-wrap">
        <div class="title">请选择适合您的 Mac 端版本</div>
        <div class="download-btn">
          <button class="intel-btn" @click.stop="downloadMac('intel')">
            下载 Intel 芯片版
          </button>
          <el-button type="primary" @click.stop="downloadMac('apple')">
            下载 Apple 芯片版
          </el-button>
        </div>
        <hr />
        <div class="tips-content">
          <p class="tips-title"><i></i> 如何确认芯片类型？</p>
          <ul class="tips-list">
            <li class="tips-item">
              <img
                src="../../assets/images/download/mac-download1.png"
                alt=""
              />
              <p class="text">
                1、在左上角，打开<span>Apple菜单</span>选择"
                <span>关于本机</span>"
              </p>
            </li>
            <li class="tips-item">
              <img
                src="../../assets/images/download/mac-download2.png"
                alt=""
              />
              <p class="text">
                2、在"<span>处理器</span>"或"<span>芯片</span>"中查看是"
                <span>Intel</span>"还是"<span>Apple</span>"
              </p>
            </li>
          </ul>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getDownloadConfig,
  getAndroidAppVersion,
  getIosAppVersion,
} from "@saas/service-api/src/index";
import { isMobile } from "@saas/components/src/utils/utils";
import QRCode from "qrcodejs2";
export default {
  data: function () {
    return {
      detectedOS: 'windows',
      activeSpecTab: 'teacher',
      downloadList: [
        {
          id: 1,
          type: "windows",
          typeText: "Windows电脑端",
          hoverText: "点击下载",
          url: process.env.VUE_APP_WIN_URL,
        },
        {
          id: 2,
          type: "mac",
          typeText: "Mac电脑端",
          hoverText: "点击下载",
          url: process.env.VUE_APP_MAC_API,
          intel: process.env.VUE_APP_MAC_API_INTEL,
        },
        {
          id: 3,
          type: "ios",
          typeText: "iPhone & iPad",
          hoverText: "扫码下载",
          url: process.env.VUE_APP_IOS_API,
        },
        {
          id: 0,
          type: "android",
          typeText: "Android",
          hoverText: "扫码下载",
          url: process.env.VUE_APP_ANDROID_API,
        },
      ],
      selectMacTypeVisible: false,
      specs: {
        teacher: [
          { label: '处理器', value: 'Intel i5 / M1 及以上' },
          { label: '运行内存', value: '8GB RAM (推荐 16GB)' },
          { label: '操作系统', value: 'Win 10+ / macOS 11.0+' },
          { label: '网络环境', value: '上行 10Mbps / 下行 20Mbps' },
        ],
        student: [
          { label: '处理器', value: '双核 2.0GHz 及以上' },
          { label: '运行内存', value: '4GB RAM' },
          { label: '操作系统', value: 'Win 10+ / macOS 11.0+' },
          { label: '网络环境', value: '2Mbps 带宽' },
        ],
      },
    };
  },
  computed: {
    currentSpecs() {
      return this.specs[this.activeSpecTab];
    },
    isMac() {
      return this.detectedOS === 'mac';
    },
  },
  mounted() {
    if (isMobile()) {
      this.$router.replace("/download-app");
      return;
    }
    this.detectOS();
    this.apiGetDownloadUrl();
  },
  methods: {
    detectOS() {
      const ua = navigator.userAgent || '';
      if (/Macintosh|MacIntel|MacPPC|Mac68K|Mac OS X/i.test(ua)) {
        this.detectedOS = 'mac';
      } else {
        this.detectedOS = 'windows';
      }
    },
    inintData() {
      Promise.all([getAndroidAppVersion(), getIosAppVersion()]).then((res) => {
        let [androidRes, iosRes] = res;
        if (androidRes.code === 1 && androidRes?.data?.url) {
          let index = this.downloadList.findIndex(
            (item) => item.type === "android"
          );
          if (index !== -1) {
            this.downloadList[index].url = androidRes.data.url;
          }
        }
        if (iosRes.code === 1 && iosRes?.data?.url) {
          let index = this.downloadList.findIndex(
            (item) => item.type === "ios"
          );
          this.downloadList[index].url = iosRes.data.url;
        }
        this.createQrcode();
      });
    },
    apiGetDownloadUrl() {
      getDownloadConfig()
        .then((data) => {
          if (data?.data?.length) {
            this.downloadList.forEach((item) => {
              let downItem = data.data.filter((ele) => ele.id === item.id);
              if (downItem.length) {
                item.url = downItem[0].url;
              }
              if (item.id === 2) {
                item.intel = downItem[0].intel;
              }
            });
          }
          this.inintData();
        })
        .catch((err) => {
          console.log(err);
          this.inintData();
        });
    },
    createQrcode() {
      let qrcodeOptions = {
        width: 96,
        height: 96,
        colorDark: "#334155",
        colorLight: "#ffffff",
        correctLevel: QRCode.CorrectLevel.H,
      };
      const androidItem = this.downloadList.find(item => item.type === 'android');
      const iosItem = this.downloadList.find(item => item.type === 'ios');
      if (androidItem?.url) {
        new QRCode(document.getElementById("qrcodeCont0"), {
          text: androidItem.url,
          ...qrcodeOptions,
        });
      }
      if (iosItem?.url) {
        new QRCode(document.getElementById("qrcodeCont3"), {
          text: iosItem.url,
          ...qrcodeOptions,
        });
      }
    },
    download(url, type) {
      if (type === "mac") {
        this.selectMacTypeVisible = true;
        return;
      } else if (["ios", "android"].includes(type)) {
        return;
      }
      if (!url) return;
      window.open(url);
    },
    downloadMac(type) {
      let macItem = this.downloadList.filter((item) => {
        return item.id === 2;
      });
      if (!macItem?.length) return;
      if (type === "intel") {
        window.open(macItem[0].intel);
      } else {
        window.open(macItem[0].url);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.download-page {
  min-height: calc(100vh - 80px);
}

.qr-container img,
.qr-container canvas {
  border-radius: 12px;
}

// Mac dialog styles
.mac-type-dialog {
  --download-dialog-bg: #ffffff;
  --download-dialog-title: #333333;
  --download-dialog-text: #666666;
  --download-dialog-border: #d8dde6;
  --download-dialog-brand-soft: rgba(0, 200, 157, 0.2);

  html.dark & {
    --download-dialog-bg: var(--mg-bg-elevated);
    --download-dialog-title: var(--mg-text-primary);
    --download-dialog-text: var(--mg-text-secondary);
    --download-dialog-border: var(--mg-border-subtle);
    --download-dialog-brand-soft: color-mix(
      in srgb,
      var(--mg-brand-primary) 16%,
      transparent
    );
  }

  ::v-deep .el-dialog__body {
    padding: 0;
    background: var(--download-dialog-bg);
  }
  ::v-deep .el-dialog {
    background: var(--download-dialog-bg);
    border-radius: 8px;
  }
  ::v-deep .el-dialog__headerbtn .el-dialog__close {
    color: var(--download-dialog-text);
  }
}
.content-wrap {
  margin: 0 auto;
  width: 721px;
  padding-top: 40px;
  .title {
    font-weight: 500;
    font-size: 24px;
    color: var(--download-dialog-title);
    line-height: 33px;
    text-align: center;
    margin-bottom: 30px;
  }
  .download-btn {
    margin-bottom: 30px;
    text-align: center;
    .el-button {
      width: 339px;
      height: 54px;
    }
    .intel-btn {
      margin-right: 43px;
      width: 339px;
      height: 54px;
      background: var(--download-dialog-brand-soft);
      border-radius: 4px;
      border: 1px solid $saas-theme-color;
      font-weight: 500;
      font-size: 16px;
      color: $saas-theme-color;
      line-height: 22px;
      cursor: pointer;
    }
  }
  hr {
    border: 0;
    border-top: 1px solid var(--download-dialog-border);
  }
  .tips-content {
    margin-top: 20px;
    padding-bottom: 77px;
    .tips-title {
      font-weight: 500;
      font-size: 16px;
      color: var(--download-dialog-title);
      line-height: 22px;
      margin-bottom: 20px;
      i {
        width: 4px;
        height: 16px;
        background: $saas-theme-color;
        border-radius: 3px;
        display: inline-block;
        margin-right: 12px;
      }
    }
    .tips-list {
      display: flex;
    }
    .tips-item {
      &:first-child {
        margin-right: 43px;
      }
      img {
        width: 339px;
        height: 198px;
        margin-bottom: 20px;
      }
      .text {
        font-size: 14px;
        color: var(--download-dialog-text);
        line-height: 20px;
        span {
          color: $saas-theme-color;
        }
      }
    }
  }
}
</style>
